import { Component, ViewChild, effect, signal, ChangeDetectionStrategy } from '@angular/core';

import { IEntityMetaData } from '@app/common-codes/_models/IEntityMetaData';
import { GenericListComponent } from '@app/common-codes/components/list-components/generic-base-list/generic-list.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { ListTableColumnComponent } from '@app/common-codes/components/list-components/list-table-column/list-table-column.component';
import { ListViewComponent } from '@app/common-codes/components/list-components/list-view/list-view.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { AccessCard } from '@app/common-codes/models/matwins-tenant/AccessCard.model';
import { LookupProfile, LookupItem } from '@app/common-codes/services/LookupService';
import { AccessCardDetailComponent } from '../access-card-detail/access-card-detail.component';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { PointOfService } from '@app/common-codes/models/matwins-tenant/PointOfService.model';

@Register(AccessCard)
@Component({
  selector: 'app-access-card-list',
  templateUrl: './access-card-list.component.html',
  standalone: true,
  styles: [`
    :host ::ng-deep .custom-filters-section {
      margin-left: auto !important;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ListViewComponent,
    AccessCardDetailComponent,
    ListTableColumnComponent,
    TranslatePipe,
    ReactiveBadgeComponent
]
})
export class AccessCardListComponent extends GenericListComponent<AccessCard> {

  override Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  override ComponentEntityClass = AccessCard;
  override entityMetaData: IEntityMetaData = new AccessCard().GetType();
  override contextMenuItems = [];
  modalEntity = signal<AccessCard | null>(null);
  @ViewChild('accessCardDetail', { read: AccessCardDetailComponent }) accessCardDetail!: AccessCardDetailComponent;

  accessCardStatusLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);

  /// Onglet actif : 'active' (non révoquées) ou 'revoked'
  activeTab = signal<'active' | 'revoked'>('active');

  selectTab(tab: 'active' | 'revoked'): void {
    this.activeTab.set(tab);
    const lv = this.listView();
    if (!lv) return;
    lv.page.set(1);
    if (this.isNested) {
      this.applyLocalFilterSortPaginate();
    } else {
      this.applyLocalPagination(0, lv.pageSize());
    }
  }

  // L'onglet Actives/Révoquées est un filtre local : il vit dans filterItems pour
  // être appliqué par tous les chemins de pagination (liste imbriquée ou API).
  protected override filterItems(items: AccessCard[]): AccessCard[] {
    const showRevoked = this.activeTab() === 'revoked';
    return super.filterItems(items.filter(item => item.isRevoked === showRevoked));
  }

  public static override get lookupProfiles(): LookupProfile[] {
    return [
      { lookupName: "AccessCardStatus" },
      { lookupName: "PointOfService" },
    ];
  }

  constructor(_alertModalService: AlertModalService) {
    super();
    this.alertModalService = _alertModalService;

    effect(() => {
      const entities = this.listView()?.entities();
      if (entities) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.accessCardStatusLookup.set(this.lookupService.getLookupData('AccessCardStatus'));
        this.pointOfServiceLookup.set(this.lookupService.getLookupData('PointOfService'));
      }
    });
  }

  onSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    this.listView()?.onSearch(value);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    // Au chargement initial, performLoadData appelle applyLocalPagination
    // avant que listView() (ViewChild) soit résolu → le filtre est ignoré.
    // On simule une sélection d'onglet pour appliquer le filtre initial.
    setTimeout(() => this.selectTab(this.activeTab()), 200);
  }

  public get canRevokeCard(): boolean {
    return this.permissionsService.UserHasSinglePermission('Permissions.Entities.AccessCard.Revoke');
  }

  override getdetailComponent(): GenetricDetailComponent<AccessCard> | undefined {
    return this.accessCardDetail;
  }
}
